import React, { useEffect, useState, useMemo, useContext } from "react"
import { SessionContext } from "context/sessionContext"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  Button,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
  Spinner,
} from "reactstrap"
import Select from "react-select"
import Swal from "sweetalert2"
import { sessionApi } from "apis/SessionApi"
import { studentMarkEntries } from "apis/MarkEntriesApi"
import { generateReports } from "apis/GradeCardApi"
import { GradeCardPreviewModal, generateGradeCardPdf } from "./Gradecardpdf"
import { classApi } from "apis/ClassListApi"

const selectStyles = {
  menu: p => ({ ...p, backgroundColor: "#fff", zIndex: 9999 }),
  menuList: p => ({ ...p, backgroundColor: "#fff", maxHeight: "200px" }),
  option: (p, s) => ({
    ...p,
    backgroundColor: s.isFocused ? "#f8f9fa" : "#fff",
    color: "#000",
    cursor: "pointer",
  }),
}


/* ─────────────────────────────────────────────
   Main GradeCard component
───────────────────────────────────────────── */
const GradeCard = () => {
  document.title = "Grade Card"

  const { Session } = useContext(SessionContext)
  const activeSessionId = Session || sessionStorage.getItem("SessionId")

  // ── Class / Section ───────────────────────────────────────────────────
  const [classOptions, setClassOptions] = useState([])
  const [classLoading, setClassLoading] = useState(false)
  const [selectedClass, setSelectedClass] = useState(null)

  // ── Exams ─────────────────────────────────────────────────────────────
  const [examOptions, setExamOptions] = useState([])
  const [examsLoading, setExamsLoading] = useState(false)
  const [selectedExams, setSelectedExams] = useState([])

  // ── Students ──────────────────────────────────────────────────────────
  const [students, setStudents] = useState([])
  const [hasGenerated, setHasGenerated] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)

  // ── Search ────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("")
  const [appliedSearch, setAppliedSearch] = useState("")

  // ── Selection ─────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState([])

  // ── Pagination ────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  // ── Report preview ────────────────────────────────────────────────────
  const [previewOpen, setPreviewOpen] = useState(false)
  const [reports, setReports] = useState([])
  const [isLoadingReports, setIsLoadingReports] = useState(false)

  /* ── Fetch all classes from getAllClass API ── */
  useEffect(() => {
    ; (async () => {
      setClassLoading(true)
      try {
        const res = await classApi.getAllClass()
        const sections = res?.data?.section_list || []
        const options = sections.map(s => ({
          value: s.UID,
          label: `${s.st_class.toUpperCase()} ${s.st_sec.toUpperCase()}`,
          st_class: s.st_class,
          st_sec: s.st_sec,
        }))
        setClassOptions(options)
      } catch (err) {
        console.error("Failed to fetch classes:", err.message)
        Swal.fire("Error", "Failed to fetch class list.", "error")
      } finally {
        setClassLoading(false)
      }
    })()
  }, [])

  /* ── Cascade reset helpers ── */
  const resetStudentState = () => {
    setStudents([])
    setSelectedIds([])
    setHasGenerated(false)
    setSearchQuery("")
    setAppliedSearch("")
    setCurrentPage(1)
    setTotalPages(1)
    setTotalCount(0)
  }

  // Reset student states and selection when session changes
  useEffect(() => {
    resetStudentState()
    setSelectedClass(null)
  }, [activeSessionId])

  /* ── Class change ── */
  const handleClassChange = option => {
    setSelectedClass(option)
    setSelectedExams([])
    setExamOptions([])
    resetStudentState()
  }

  /* ── Fetch exams when class changes ── */
  useEffect(() => {
    if (!selectedClass) {
      setExamOptions([])
      setSelectedExams([])
      return
    }
    ; (async () => {
      setExamsLoading(true)
      try {
        const res = await generateReports.getAllExams({
          class: selectedClass.st_class,
          session_id: activeSessionId ? parseInt(activeSessionId, 10) : null,
        })
        if (res?.data?.status === 200 && Array.isArray(res.data.exams)) {
          const options = res.data.exams.map(e => ({
            value: e.id,
            label: e.exam_name,
          }))
          setExamOptions(options)
          setSelectedExams(options)
        } else {
          setExamOptions([])
          setSelectedExams([])
          console.warn("Failed to fetch exams:", res?.data?.msg)
        }
      } catch (err) {
        console.error("Failed to fetch exams:", err.message)
        setExamOptions([])
      } finally {
        setExamsLoading(false)
      }
    })()
  }, [selectedClass])

  /* ── Fetch students helper ── */
  const fetchStudents = async (page = 1, search = "") => {
    if (!selectedClass) return

    setIsLoading(true)
    try {
      const res = await studentMarkEntries.getMarkEntries({
        session_id: parseInt(activeSessionId, 10),
        st_class: selectedClass.st_class,
        st_sec: selectedClass.st_sec,
        page,
        search,
      })

      if (res?.data?.status === 200 && Array.isArray(res.data.entries)) {
        const seen = new Set()
        const unique = []
        res.data.entries.forEach(e => {
          if (!seen.has(e.st_code)) {
            seen.add(e.st_code)
            unique.push({ st_code: e.st_code, st_name: e.st_name })
          }
        })
        setStudents(unique)
        setTotalPages(res.data.total_pages ?? 1)
        setTotalCount(res.data.total_count ?? res.data.entries.length)
        setCurrentPage(res.data.page_number ?? page)
        setHasGenerated(true)
      } else {
        Swal.fire(
          "Error",
          res?.data?.msg || "Failed to fetch student data.",
          "error"
        )
      }
    } catch (err) {
      console.error("Failed to fetch students:", err.message)
      Swal.fire(
        "Error",
        "Failed to fetch student data. Please try again.",
        "error"
      )
    } finally {
      setIsLoading(false)
    }
  }

  /* ── Load students ── */
  const handleLoadStudents = async () => {
    if (!selectedClass)
      return Swal.fire(
        "Validation",
        "Please select a Class / Section.",
        "warning"
      )

    if (selectedExams.length === 0)
      return Swal.fire(
        "Validation",
        "Please select at least one Exam.",
        "warning"
      )

    resetStudentState()
    setIsGenerating(true)
    await fetchStudents(1, "")
    setIsGenerating(false)
  }

  useEffect(() => {
    if (!hasGenerated) return
    if (searchQuery === appliedSearch) return

    const delayDebounceFn = setTimeout(() => {
      setAppliedSearch(searchQuery)
      fetchStudents(1, searchQuery)
    }, 500)

    return () => clearTimeout(delayDebounceFn)
  }, [searchQuery, hasGenerated, appliedSearch])

  /* ── Search ── */
  const handleSearch = () => {
    if (hasGenerated) {
      setAppliedSearch(searchQuery)
      fetchStudents(1, searchQuery)
    }
  }
  const handleSearchKeyDown = e => {
    if (e.key === "Enter") handleSearch()
  }

  const handlePageChange = page => {
    if (page < 1 || page > totalPages) return
    fetchStudents(page, appliedSearch)
  }

  const filteredData = students

  /* ── Checkbox helpers ── */
  const allChecked =
    filteredData.length > 0 &&
    filteredData.every(s => selectedIds.includes(s.st_code))
  const isIndeterminate =
    filteredData.some(s => selectedIds.includes(s.st_code)) && !allChecked

  const handleSelectAll = e => {
    if (e.target.checked) {
      setSelectedIds(prev => {
        const next = [...prev]
        filteredData.forEach(s => {
          if (!next.includes(s.st_code)) {
            next.push(s.st_code)
          }
        })
        return next
      })
    } else {
      setSelectedIds(prev =>
        prev.filter(id => !filteredData.some(s => s.st_code === id))
      )
    }
  }
  const handleSelectOne = (e, id) => {
    setSelectedIds(prev =>
      e.target.checked ? [...prev, id] : prev.filter(sid => sid !== id)
    )
  }

  /* ── Generate grade cards ── */
  const handleOpenGradeCard = async () => {
    if (selectedIds.length === 0)
      return Swal.fire(
        "Validation",
        "Please select at least one student.",
        "warning"
      )

    if (selectedExams.length === 0)
      return Swal.fire(
        "Validation",
        "Please select at least one Exam.",
        "warning"
      )

    setPreviewOpen(true)
    setIsLoadingReports(true)
    setReports([])

    try {
      const res = await generateReports.getReportGenerated({
        session_id: parseInt(activeSessionId, 10),
        st_codes: selectedIds,
        exam_ids: selectedExams.map(e => e.value),
      })

      const data = res?.data ?? res

      if (
        data?.status === 200 &&
        Array.isArray(data.reports) &&
        data.reports.length > 0
      ) {
        const rootAverages = data.subject_averages || []
        const reportsWithAverages = data.reports.map(r => {
          const apiAverages = rootAverages.length > 0 ? rootAverages : (r.subject_averages || [])
          
          const exam1Name = r.exams?.[0]?.exam_name
          const exam2Name = r.exams?.[1]?.exam_name
          
          const cleanExam1 = exam1Name ? exam1Name.toLowerCase().replace(/[^a-z0-9]/g, "") : null
          const cleanExam2 = exam2Name ? exam2Name.toLowerCase().replace(/[^a-z0-9]/g, "") : null

          const subject_avg = apiAverages.map(item => {
            let avgVal = null
            let term1Val = null
            let term2Val = null

            Object.keys(item).forEach(key => {
              if (key !== "subject_code" && key !== "subject_name") {
                const val = item[key]
                const cleanKey = key.toLowerCase().replace(/[^a-z0-9]/g, "")
                
                // Match Term 1
                if (cleanKey.includes("term1") || cleanKey.includes("t1")) {
                  term1Val = val
                } else if (cleanExam1 && (cleanKey.includes(cleanExam1) || cleanExam1.includes(cleanKey))) {
                  term1Val = val
                }
                
                // Match Term 2
                if (cleanKey.includes("term2") || cleanKey.includes("t2")) {
                  term2Val = val
                } else if (cleanExam2 && (cleanKey.includes(cleanExam2) || cleanExam2.includes(cleanKey))) {
                  term2Val = val
                }

                if (val !== null && val !== undefined && val !== "") {
                  avgVal = val
                }
              }
            })
            return {
              subject_name: item.subject_name,
              subject_code: item.subject_code,
              term1_avg: term1Val,
              term2_avg: term2Val,
              average: avgVal,
            }
          })
          return {
            ...r,
            subject_avg,
            group: data.group || "9",
          }
        })

        setReports(reportsWithAverages)
        if (data.errors?.length > 0) {
          console.warn("Report generation errors:", data.errors)
        }
      } else {
        const errMsg = data?.msg || "No report data returned."
        Swal.fire("Error", errMsg, "error")
        setPreviewOpen(false)
      }
    } catch (err) {
      console.error("Failed to generate reports:", err.message)
      Swal.fire(
        "Error",
        err.response?.data?.msg ||
        "Failed to generate grade cards. Please try again.",
        "error"
      )
      setPreviewOpen(false)
    } finally {
      setIsLoadingReports(false)
    }
  }

  /* ── Download PDF ── */
  const handleDownloadPdf = async () => {
    if (!reports || reports.length === 0) return
    await generateGradeCardPdf(reports)
  }

  /* ── Reset ── */
  const handleReset = () => {
    setSelectedClass(null)
    setSelectedExams([])
    setExamOptions([])
    resetStudentState()
  }

  /* ── Table body ── */
  const renderTableBody = () => {
    if (!hasGenerated && !isLoading) {
      return (
        <tr>
          <td colSpan={3} className="text-center py-5 text-muted">
            <i
              className="bx bx-list-ul"
              style={{
                fontSize: "2rem",
                display: "block",
                marginBottom: "8px",
              }}
            />
            Select a Class / Section and click <strong>Load Students</strong>.
          </td>
        </tr>
      )
    }
    if (isLoading) {
      return (
        <tr>
          <td colSpan={3} className="text-center py-5 text-muted">
            <i
              className="bx bx-loader-alt bx-spin"
              style={{
                fontSize: "2rem",
                display: "block",
                marginBottom: "8px",
              }}
            />
            Loading students…
          </td>
        </tr>
      )
    }
    if (filteredData.length === 0) {
      return (
        <tr>
          <td colSpan={3} className="text-center py-5 text-muted">
            <i
              className="bx bx-search-alt"
              style={{
                fontSize: "2rem",
                display: "block",
                marginBottom: "8px",
              }}
            />
            {appliedSearch
              ? "No students match your search."
              : "No student records found."}
          </td>
        </tr>
      )
    }
    return filteredData.map(student => (
      <tr key={student.st_code}>
        <td className="text-center">
          <Input
            type="checkbox"
            checked={selectedIds.includes(student.st_code)}
            onChange={e => handleSelectOne(e, student.st_code)}
          />
        </td>
        <td className="text-center">{student.st_code}</td>
        <td>{student.st_name}</td>
      </tr>
    ))
  }

  // ── Pagination ───────────────────────────────────────────────────────────
  const renderPagination = () => {
    if (!hasGenerated || totalPages <= 1) return null
    const pages = []
    const maxVisible = 5
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1)
    for (let i = start; i <= end; i++) pages.push(i)

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          Showing page <strong>{currentPage}</strong> of{" "}
          <strong>{totalPages}</strong>
          &nbsp;|&nbsp; Total: <strong>{totalCount}</strong> students
        </small>
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(1)}>
              «
            </button>
          </li>
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage - 1)}
            >
              ‹
            </button>
          </li>
          {pages.map(p => (
            <li
              key={p}
              className={`page-item ${p === currentPage ? "active" : ""}`}
            >
              <button className="page-link" onClick={() => handlePageChange(p)}>
                {p}
              </button>
            </li>
          ))}
          <li
            className={`page-item ${currentPage === totalPages ? "disabled" : ""
              }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage + 1)}
            >
              ›
            </button>
          </li>
          <li
            className={`page-item ${currentPage === totalPages ? "disabled" : ""
              }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(totalPages)}
            >
              »
            </button>
          </li>
        </ul>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Container fluid={true}>
            <Breadcrumb title="Examination" breadcrumbItem="Grade Card" />

            {/* ── Filter Section ── */}
            <div className="p-3 mb-4 rounded bg-white">
              <Row>
                {/* Class / Section */}
                <Col lg={3} md={6} className="mb-3">
                  <label className="form-label fw-semibold mb-1">
                    Class / Section <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="st_class"
                    classNamePrefix="select2-selection"
                    placeholder={
                      classLoading
                        ? "Loading classes…"
                        : classOptions.length === 0
                          ? "No classes available"
                          : "Select Class / Section..."
                    }
                    options={classOptions}
                    value={selectedClass}
                    onChange={handleClassChange}
                    isLoading={classLoading}
                    isDisabled={classLoading || classOptions.length === 0}
                    styles={selectStyles}
                  />
                </Col>

                {/* Exam */}
                <Col lg={3} md={6} className="mb-3">
                  <label className="form-label fw-semibold mb-1">
                    Exam <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="exams"
                    isMulti
                    classNamePrefix="select2-selection"
                    placeholder={
                      examsLoading
                        ? "Loading exams…"
                        : !selectedClass
                          ? "Select Class / Section first"
                          : examOptions.length === 0
                            ? "No exams available"
                            : "Select Exams..."
                    }
                    options={examOptions}
                    value={selectedExams}
                    onChange={setSelectedExams}
                    isLoading={examsLoading}
                    isDisabled={examsLoading || !selectedClass || examOptions.length === 0}
                    styles={selectStyles}
                  />
                </Col>
              </Row>

              <Row className="mt-1">
                <Col>
                  <Button
                    color="primary"
                    className="me-2"
                    onClick={handleLoadStudents}
                    disabled={isLoading || isGenerating || !selectedClass || !selectedExams || selectedExams.length === 0}
                  >
                    <i className="bx bx-list-ul me-1" />
                    {isGenerating ? "Loading…" : "Load Students"}
                  </Button>
                  <Button
                    color="secondary"
                    outline
                    onClick={handleReset}
                    disabled={isLoading}
                  >
                    <i className="bx bx-reset me-1" />
                    Reset
                  </Button>
                </Col>
              </Row>
            </div>

            {/* ── Search + Generate ── */}
            <Row className="mb-3 align-items-center">
              <Col lg={5} md={12} className="mb-2 mb-lg-0">
                <div className="d-flex gap-2">
                  <div style={{ position: "relative", width: "100%" }}>
                    <Input
                      type="text"
                      placeholder="Search by Student Name or Student Code"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      disabled={!hasGenerated}
                      style={{ fontSize: "13px", paddingRight: "30px" }}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchQuery("")
                          setAppliedSearch("")
                          fetchStudents(1, "")
                        }}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          border: "none",
                          background: "none",
                          cursor: "pointer",
                          fontSize: "12px",
                          padding: 0,
                          color: "#6c757d",
                          zIndex: 5,
                        }}
                      >
                        ❌
                      </button>
                    )}
                  </div>
                  <Button
                    color="primary"
                    onClick={handleSearch}
                    disabled={!hasGenerated || isLoading}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    <i className="bx bx-search me-1" />
                    Search
                  </Button>
                </div>
              </Col>

              <Col lg={7} md={12}>
                <div className="d-flex align-items-center justify-content-lg-end gap-2 flex-wrap">
                  {selectedIds.length > 0 && (
                    <span
                      className="badge rounded-pill"
                      style={{
                        background: "#ede9fe",
                        color: "#5b21b6",
                        fontSize: "13px",
                        padding: "6px 14px",
                        fontWeight: 600,
                      }}
                    >
                      <i className="bx bx-check-circle me-1" />
                      Selected: {selectedIds.length}{" "}
                      {selectedIds.length === 1 ? "Student" : "Students"}
                    </span>
                  )}
                  <Button
                    color="success"
                    size="md"
                    onClick={handleOpenGradeCard}
                    disabled={selectedIds.length === 0}
                  >
                    <i className="bx bx-id-card me-1" />
                    Generate Grade Card
                  </Button>
                </div>
              </Col>
            </Row>

            {/* ── Table ── */}
            <div className="table-responsive">
              <Table className="table table-striped table-bordered">
                <thead>
                  <tr className="text-center table-secondary">
                    <th style={{ width: "48px" }}>
                      <Input
                        type="checkbox"
                        checked={allChecked}
                        innerRef={el => {
                          if (el) el.indeterminate = isIndeterminate
                        }}
                        onChange={handleSelectAll}
                        disabled={!hasGenerated || filteredData.length === 0}
                      />
                    </th>
                    <th className="text-center" style={{ width: "140px" }}>
                      Student Code
                    </th>
                    <th className="text-start">Student Name</th>
                  </tr>
                </thead>
                <tbody>{renderTableBody()}</tbody>
              </Table>
            </div>
            {renderPagination()}
          </Container>
        </div>
      </div>
      <GradeCardPreviewModal
        isOpen={previewOpen}
        toggle={() => setPreviewOpen(false)}
        reports={reports}
        isLoadingReports={isLoadingReports}
        onDownloadPdf={handleDownloadPdf}
      />
    </React.Fragment>
  )
}

export default GradeCard
